import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { SearchSelect } from "@zendesk/self-service-components";
import { Spinner } from "@zendeskgarden/react-loaders";
import t from "@zendesk/client-i18n-tools";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import debounce from "lodash/debounce";
import * as userSegmentActions from "../../../../actions/userSegment";
import * as groupActions from "../../../../actions/groups";
import * as organizationActions from "../../../../actions/organizations";
import * as tagActions from "../../../../actions/tags";

import styles from "./index.css";

const ITEM_PROPS_MAPPING = {
  groups: {
    getItemId: item => item.id,
    renderItem: item => item.name,
  },
  organizations: {
    getItemId: item => item.id,
    renderItem: item => item.name,
  },
  tags: {
    getItemId: item => item,
    renderItem: item => item,
  },
};

const ACTIONS_MAPPING = {
  groups: groupActions,
  organizations: organizationActions,
  tags: tagActions,
};

const USER_SEGMENT_ACTIONS_MAPPING = {
  groups: {
    add: userSegmentActions.addGroup,
    remove: userSegmentActions.removeGroup,
    change: userSegmentActions.changeGroup,
  },
  organizations: {
    add: userSegmentActions.addOrganization,
    remove: userSegmentActions.removeOrganization,
    change: userSegmentActions.changeOrganization,
  },
  tags: {
    add: userSegmentActions.addTag,
    remove: userSegmentActions.removeTag,
    change: userSegmentActions.changeTag,
  },
};

export class AutocompleteItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      query: "",
    };

    this.fetchAutocomplete = debounce(this.fetchAutocomplete.bind(this), 300);
    this.onSearchTextChanged = this.onSearchTextChanged.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onRemove = this.onRemove.bind(this);
  }

  componentWillMount() {
    const { isInitialFetching, isInitialLoaded, fetchInitial } = this.props;

    if (!isInitialLoaded && !isInitialFetching) {
      fetchInitial();
    }
  }

  onSearchTextChanged(query) {
    this.setState({
      query,
    });

    this.fetchAutocomplete(query);
  }

  fetchAutocomplete(query) {
    const { autocomplete, fetchAutocomplete } = this.props;
    const autocompleteResult = autocomplete[query];

    if (
      !autocompleteResult ||
      (!autocompleteResult.isLoaded && !autocompleteResult.isLoading)
    ) {
      fetchAutocomplete(query);
    }
  }

  onChange(selectedItem) {
    const { newItem, item, change, onNewAdd, getItemId } = this.props;

    if (!selectedItem) {
      return;
    }

    if (newItem) {
      onNewAdd(selectedItem);
    } else if (getItemId(item) !== getItemId(selectedItem)) {
      change(item, selectedItem);
    }

    this.setState({ query: "" });
  }

  onRemove() {
    const { newItem, item, remove, onNewRemove } = this.props;

    if (newItem) {
      onNewRemove();
    } else {
      remove(item);
    }
  }

  getApplicable(items) {
    const { excludedItems, getItemId } = this.props;
    const excludedIds = excludedItems.map(getItemId);

    return items.filter(item => !excludedIds.includes(getItemId(item)));
  }

  renderItems() {
    const { query } = this.state;
    const {
      initialList,
      isInitialFetching,
      autocomplete,
      renderItem,
      getItemId,
    } = this.props;

    const isIntial = query.length === 0;
    const results = autocomplete[query];

    if (isIntial && isInitialFetching) {
      return (
        <div
          className={styles.loader}
          data-test-id="user-segment-tag-list-loader"
        >
          <Spinner />
        </div>
      );
    }

    if (!isIntial && (!results || results.isFetching)) {
      return (
        <div
          className={styles.loader}
          data-test-id="user-segment-tag-list-loader"
        >
          <Spinner />
        </div>
      );
    }

    const itemList = isIntial ? initialList : results.list;
    const items =
      itemList.length === 0
        ? [
            <SearchSelect.Item disabled key="no-results">
              No results
            </SearchSelect.Item>,
          ]
        : this.getApplicable(itemList).map(item => (
            <SearchSelect.Item key={getItemId(item)} value={item}>
              {renderItem(item)}
            </SearchSelect.Item>
          ));

    return [
      <SearchSelect.Item
        key="remove"
        onClick={this.onRemove}
        testId="remove-item"
      >
        {t("knowledge-user-segments.userSegment.remove")}
      </SearchSelect.Item>,
      <SearchSelect.Separator key="separator" />,
      ...items,
    ];
  }

  render() {
    const { query } = this.state;
    const { item, type, renderItem } = this.props;

    const selected = item
      ? renderItem(item)
      : t("knowledge-user-segments.userSegment.selectNew");

    return (
      <div className={styles.groupContainer}>
        <SearchSelect
          testId={`user-segment-${type}`}
          dir={t.dir}
          maxHeight="250px"
          selected={selected}
          searchText={query}
          onSearchTextChanged={this.onSearchTextChanged}
          onChange={this.onChange}
          validation={item && item.deleted ? "error" : null}
          placeholder={t(
            "knowledge-user-segments.userSegment.searchPlaceholder"
          )}
        >
          {this.renderItems()}
        </SearchSelect>
      </div>
    );
  }
}

AutocompleteItem.propTypes = {
  newItem: PropTypes.bool,
  item: PropTypes.any,
  initialList: PropTypes.array,
  isInitialFetching: PropTypes.bool,
  isInitialLoaded: PropTypes.bool,
  fetchInitial: PropTypes.func,
  onNewAdd: PropTypes.func,
  onNewRemove: PropTypes.func,
  autocomplete: PropTypes.object,
  fetchAutocomplete: PropTypes.func,
  change: PropTypes.func,
  remove: PropTypes.func,
  excludedItems: PropTypes.array,
  renderItem: PropTypes.func,
  getItemId: PropTypes.func,
  type: PropTypes.string,
};

AutocompleteItem.defaultProps = {
  newItem: false,
  item: null,
  initialList: [],
  excludedItems: [],
  isInitialFetching: false,
  isInitialLoaded: false,
};

const mapStateToProps = (state, { type }) => ({
  initialList: state[type].initialList,
  isInitialFetching: state[type].isInitialFetching,
  isInitialLoaded: state[type].isInitialLoaded,
  autocomplete: state[type].autocomplete,
  excludedItems: state.userSegment[type],
  ...ITEM_PROPS_MAPPING[type],
});

const mapDispatchToProps = (dispatch, { type }) =>
  bindActionCreators(
    { ...ACTIONS_MAPPING[type], ...USER_SEGMENT_ACTIONS_MAPPING[type] },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AutocompleteItem);



// WEBPACK FOOTER //
// ./src/components/AutocompleteList/components/AutocompleteItem/index.js